import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { getOrderStatus } from "../../api/ticketsPlatform/checkout";
import Navbar from "../../components/tickets/navBar";
import Footer from "../../components/tickets/footer";
import { usePixel } from "../../hooks/usePixel";
import { useTranslation } from "react-i18next";

const POLL_INTERVAL = 3000; // poll every 3 seconds
const MAX_POLLS = 20; // give up after ~60 seconds

export default function OrderStatus() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const orderNumber = location.state?.orderNumber;
  const pixels = location.state?.pixels || [];

  const [status, setStatus] = useState("polling"); // polling | paid | pending | failed
  const [orderData, setOrderData] = useState(null);
  const pollCount = useRef(0);

  const { trackEvent } = usePixel(pixels);

  useEffect(() => {
    if (!orderNumber) {
      navigate("/tickets-store/");
      return;
    }

    const poll = async () => {
      try {
        const data = await getOrderStatus(orderNumber);
        setOrderData(data);

        const s = data.status?.toLowerCase();

        if (s === "paid" || s === "completed") {
          setStatus("paid");
          trackEvent("Purchase", {
            value: data.total,
            currency: data.currency,
            content_ids: [data.order_number],
          });
          return; // stop polling
        }

        if (s === "failed" || s === "cancelled") {
          setStatus("failed");
          return;
        }

        // Still pending — keep polling up to max
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          setStatus("pending"); // timed out — show pending message
          return;
        }

        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    // Start polling after a short delay to give backend time to process
    const timer = setTimeout(poll, 1500);
    return () => clearTimeout(timer);
  }, [orderNumber, navigate]);

  return (
    <div className="bg-[#021610] text-white min-h-screen flex flex-col">
      {/* NAVBAR */}
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 pt-20 pb-16">
        <div className="w-full max-w-md text-center">
          {/* ── POLLING STATE ── */}
          {status === "polling" && (
            <div className="space-y-6">
              <Spinner />
              <div>
                <h2 className="text-xl font-bold">
                  {t("orderStatus.verifying")}
                </h2>
                <p className="text-sm text-gray-400 mt-2">
                  {t("orderStatus.verifyingHint")}
                </p>
              </div>
              <OrderRef orderNumber={orderNumber} />
            </div>
          )}

          {/* ── PAID / SUCCESS ── */}
          {status === "paid" && (
            <div className="space-y-6">
              {/* SUCCESS ICON */}
              <div className="w-20 h-20 mx-auto rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                <span className="text-4xl">✓</span>
              </div>

              <div>
                <p className="text-[10px] tracking-widest text-green-400 uppercase mb-2">
                  {t("orderStatus.paymentComplete")}
                </p>
                <h2 className="text-2xl md:text-3xl font-bold">
                  {t("orderStatus.confirmed")}
                </h2>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">
                  {t("orderStatus.confirmedHint")}
                </p>
              </div>

              <OrderRef orderNumber={orderNumber} />

              {/* DIVIDER */}
              <div className="border-t border-green-900/30" />

              {/* ACTIONS */}
              <div className="space-y-3">
                <button
                  onClick={() => navigate("/tickets-store/")}
                  className="w-full py-3 rounded-xl bg-green-500 text-black font-bold tracking-widest uppercase text-sm hover:bg-green-400 transition"
                >
                  {t("orderStatus.seeMoreEvents")}
                </button>
              </div>
            </div>
          )}

          {/* ── PENDING (timed out polling) ── */}
          {status === "pending" && (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                <span className="text-4xl">⏳</span>
              </div>

              <div>
                <p className="text-[10px] tracking-widest text-yellow-400 uppercase mb-2">
                  {t("orderStatus.pendingLabel")}
                </p>
                <h2 className="text-2xl font-bold">
                  {t("orderStatus.pendingTitle")}
                </h2>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">
                  {t("orderStatus.pendingHint")}
                </p>
              </div>

              <OrderRef orderNumber={orderNumber} />

              <div className="border-t border-green-900/30" />

              <button
                onClick={() => navigate("/tickets-store/")}
                className="w-full py-3 rounded-xl border border-green-500 text-green-400 font-bold tracking-widest uppercase text-sm hover:bg-green-500 hover:text-black transition"
              >
                {t("orderStatus.backToEvents")}
              </button>
            </div>
          )}

          {/* ── FAILED ── */}
          {status === "failed" && (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <span className="text-4xl">✕</span>
              </div>

              <div>
                <p className="text-[10px] tracking-widest text-red-400 uppercase mb-2">
                  {t("orderStatus.failedLabel")}
                </p>
                <h2 className="text-2xl font-bold">
                  {t("orderStatus.failedTitle")}
                </h2>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">
                  {t("orderStatus.failedHint")}
                </p>
              </div>

              <OrderRef orderNumber={orderNumber} />

              <div className="border-t border-green-900/30" />

              <button
                onClick={() => navigate("/tickets-store/")}
                className="w-full py-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-300 font-bold tracking-widest uppercase text-sm hover:bg-red-500/30 transition"
              >
                {t("orderStatus.backToEvents")}
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ── SUB COMPONENTS ── */

function OrderRef({ orderNumber }) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center gap-2 bg-[#031c14] border border-green-900/30 rounded-xl px-4 py-2 mx-auto">
      <p className="text-[10px] tracking-widest text-gray-400 uppercase">
        {t("orderStatus.reference")}
      </p>
      <p className="text-sm font-mono text-green-400">#{orderNumber}</p>
    </div>
  );
}

function Spinner() {
  return (
    <div className="w-16 h-16 mx-auto">
      <div
        className="w-full h-full rounded-full border-4 border-green-900/30 border-t-green-500"
        style={{ animation: "spin 1s linear infinite" }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
